import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api, { StreamSource, ChatStreamEvent, ConversationMessage, ToolCallSummary } from '../services/api';
import { mutate } from 'swr';
import { SourceItem, dedupeSources } from './SourcesPanel';
import { useAuth } from '../context/AuthContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UpMaster {
  id: number;
  uid: string;
  name: string;
  face: string;
  video_count: number;
}

interface Props {
  upmasters: UpMaster[];
  conversationId: number | null;
  onConversationCreated: (id: number) => void;
  onConversationUpdated: () => void;
  /** 锁定 up 主模式：传入时隐藏多选下拉，selectedIds 固定为这些 id */
  fixedUpmasterIds?: number[];
  /** 电子教练模式（v21）：隐藏知识库多选，请求带 coach_id，单窗口由 CoachView 管理 */
  coach?: { id: number; name: string; avatar: string; bio: string } | null;
}

interface Message {
  id: number;
  question: string;
  answer: string;
  sources: StreamSource[];
  created_at: string;
  tool_calls?: ToolCallSummary[];
}

const SUGGESTIONS = [
  { icon: '🔥', text: '如何科学减脂？' },
  { icon: '💪', text: '增肌期饮食建议' },
  { icon: '🏋️', text: '今天练了卧推 60kg 5x5，帮我记录' },
  { icon: '📋', text: '帮我生成一个 4 天增肌训练计划' },
];

// 工具名 → 中文短标签（输入框上方工具条展示）
const TOOL_LABELS: Record<string, string> = {
  record_training: '记录训练',
  query_training: '查训练历史',
  get_calendar: '训练日历',
  generate_training_plan: '生成训练计划',
  search_knowledge_base: '搜知识库',
};

function formatToolInvocation(name: string, args: any, maxLen: number = 100): string {
  if (!args || typeof args !== 'object' || Object.keys(args).length === 0) {
    return `${name}()`;
  }
  const parts: string[] = [];
  for (const [key, val] of Object.entries(args)) {
    let valStr: string;
    if (typeof val === 'string') {
      valStr = val.length > 30 ? `"${val.slice(0, 27)}..."` : `"${val}"`;
    } else if (typeof val === 'object' && val !== null) {
      valStr = JSON.stringify(val);
      if (valStr.length > 25) valStr = valStr.slice(0, 22) + '...';
    } else {
      valStr = String(val);
    }
    parts.push(`${key}=${valStr}`);
  }
  const full = `${name}(${parts.join(', ')})`;
  if (full.length > maxLen) {
    return full.slice(0, maxLen - 4) + '...)';
  }
  return full;
}

// 打字机节奏：整段到达时按 ~3 字符/20ms 递增 reveal（完成时全部显示）
const TYPEWRITER_STEP = 3;
const TYPEWRITER_INTERVAL_MS = 20;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ChatPanel: React.FC<Props> = ({ upmasters, conversationId, onConversationCreated, onConversationUpdated, fixedUpmasterIds, coach }) => {
  const { user } = useAuth();
  // 多源选择：selectedIds 为空数组 = 全部知识库（不加 filter）
  const isFixed = !!fixedUpmasterIds && fixedUpmasterIds.length > 0;
  const isCoach = !!coach;
  const [selectedIds, setSelectedIds] = useState<number[]>(fixedUpmasterIds ?? []);
  const [selOpen, setSelOpen] = useState(false);

  // ---- state ----
  const historyUpmasterId = selectedIds.length === 1 ? selectedIds[0] : 0;
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Message[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 工具列表（输入框上方展示，来自 GET /api/tools）
  const [tools, setTools] = useState<{ name: string; description: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.getTools()
      .then(r => { if (!cancelled) setTools(r.data?.data || []); })
      .catch(() => { /* 工具条非关键，失败静默 */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // streaming
  const [useStreaming, setUseStreaming] = useState(true);
  const [streamingText, setStreamingText] = useState('');          // live tokens
  const [toolStatus, setToolStatus] = useState('');               // tool call status
  const [streamingSources, setStreamingSources] = useState<StreamSource[]>([]); // 流式回答已到达的来源

  // error
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Refs to avoid stale closure issues when pushing to history after stream
  const answerRef = useRef('');
  const sourcesRef = useRef<StreamSource[]>([]);
  const conversationRef = useRef<number | null>(conversationId);
  const loadingRef = useRef(false);
  const streamConvRef = useRef<number | null>(null);
  const streamCreatedConvRef = useRef<number | null>(null); // 本次流新建的会话 id（新对话场景）
  const pendingIdRef = useRef<number | null>(null);         // 乐观 UI：pending 提问气泡的 id
  const recordedTrainingRef = useRef(false);               // 本次流是否调用过 record_training（用于刷新训练日历缓存）
  const toolCallsRef = useRef<ToolCallSummary[]>([]);      // 本次回答的工具调用摘要（tool_calls + tool_results 事件）
  const streamChatIdRef = useRef<number | null>(null);     // done 事件带出的 chat_history id
  // 打字机：整段到达路径（token 事件数 ≤ 1）用 interval 逐字符 reveal，新消息/切会话/卸载时必须清理
  const [typewriterActive, setTypewriterActive] = useState(false);
  const tokenCountRef = useRef(0);                          // 本次流收到的 token 事件数（1 = 整段一次到达）
  const typewriterFullRef = useRef('');                     // 正在 reveal 的完整文本
  const typewriterLenRef = useRef(0);                       // 已 reveal 的字符数（interval 内读取，防闭包过期）
  const typewriterTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const typewriterActiveRef = useRef(false);                // 同步标记，供事件回调 / interval 内读取
  const typewriterPendingRef = useRef(false);               // 流已结束但 reveal 未完成 → 完成后才落库到 history

  // 工具调用折叠条：每条消息一个展开状态；反馈：chat_id → 当前选择（1/-1/0 未选）
  const [openToolCalls, setOpenToolCalls] = useState<Set<number>>(new Set());
  // 参考资料内联折叠块：每条消息一个展开状态（流式块用固定 key -1）
  const [openSources, setOpenSources] = useState<Set<number>>(new Set());
  const [feedbackSel, setFeedbackSel] = useState<Record<number, number>>({});

  // Keep ref in sync so stream callbacks see the latest conversation id
  useEffect(() => {
    conversationRef.current = conversationId;
  }, [conversationId]);

  // 锁定模式：fixed 值变化时同步 selectedIds（不渲染多选下拉）
  useEffect(() => {
    if (fixedUpmasterIds) setSelectedIds(fixedUpmasterIds);
  }, [fixedUpmasterIds]);

  /** 流结束后落库：把本次回答按 pendingId 更新到 history 并清空流式框（打字机完成后再调）。 */
  const finalizeStream = useCallback(() => {
    const created = streamCreatedConvRef.current;
    const sameConv = created != null
      ? conversationRef.current === created
      : streamConvRef.current === conversationRef.current;
    if (sameConv) {
      const pid = pendingIdRef.current;
      if (pid != null) {
        setHistory(prev => prev.map(m =>
          m.id === pid
            ? {
                ...m,
                id: streamChatIdRef.current ?? m.id,
                answer: answerRef.current,
                sources: sourcesRef.current,
                tool_calls: toolCallsRef.current,
              }
            : m,
        ));
        pendingIdRef.current = null;
      }
      setStreamingText(''); // 内容已入 history，清空流式框避免双回答框
    }
    setStreamingSources([]);
    // 训练记录成功后，让所有 'calendar' 缓存失效并后台重取（日历页有缓存即显示 → 不闪加载）
    if (recordedTrainingRef.current) {
      mutate((key) => Array.isArray(key) && key[0] === 'calendar', undefined, { revalidate: true });
      recordedTrainingRef.current = false;
    }
    onConversationUpdated();
  }, [onConversationUpdated]);

  /** 停止打字机 interval（不触发落库）。 */
  const stopTypewriter = useCallback(() => {
    if (typewriterTimerRef.current) {
      clearInterval(typewriterTimerRef.current);
      typewriterTimerRef.current = null;
    }
    typewriterActiveRef.current = false;
    setTypewriterActive(false);
  }, []);

  /** 清理打字机：停止 interval + 丢弃未完成的落库标记（用于新对话/会话切换）。 */
  const resetTypewriter = useCallback(() => {
    stopTypewriter();
    typewriterPendingRef.current = false;
    setStreamingSources([]);
  }, [stopTypewriter]);

  /** 启动打字机：从空开始按 3 字符/20ms 递增 reveal，完成时全部显示并落库。 */
  const startTypewriter = useCallback((full: string) => {
    stopTypewriter();
    typewriterFullRef.current = full;
    typewriterLenRef.current = 0;
    if (!full) return; // 空文本无需打字机
    typewriterActiveRef.current = true;
    setTypewriterActive(true);
    setStreamingText('');
    typewriterTimerRef.current = setInterval(() => {
      const next = Math.min(typewriterLenRef.current + TYPEWRITER_STEP, typewriterFullRef.current.length);
      typewriterLenRef.current = next;
      setStreamingText(typewriterFullRef.current.slice(0, next));
      if (next >= typewriterFullRef.current.length) {
        if (typewriterTimerRef.current) {
          clearInterval(typewriterTimerRef.current);
          typewriterTimerRef.current = null;
        }
        typewriterActiveRef.current = false;
        setTypewriterActive(false);
        if (typewriterPendingRef.current) {
          typewriterPendingRef.current = false;
          finalizeStream();
        }
      }
    }, TYPEWRITER_INTERVAL_MS);
  }, [stopTypewriter, finalizeStream]);

  /** 打字机期间点击文本/按钮：直接显示全部（并落库）。 */
  const revealAllTypewriter = useCallback(() => {
    if (!typewriterActiveRef.current) return;
    stopTypewriter();
    typewriterLenRef.current = typewriterFullRef.current.length;
    setStreamingText(typewriterFullRef.current);
    if (typewriterPendingRef.current) {
      typewriterPendingRef.current = false;
      finalizeStream();
    }
  }, [stopTypewriter, finalizeStream]);

  // ---- effects ----
  useEffect(() => {
    if (conversationId == null) {
      // 流式进行中：会话 id 抖动（如 Hub 会话帧）不得清空历史/乐观 UI
      if (loadingRef.current) return;
      setHistory([]);
      setStreamingText('');
      resetTypewriter(); // 新对话：清理打字机 timer
      return;
    }
    if (loadingRef.current) return; // 流式进行中：面板由 sources 事件填充，历史由 done 分支推送
    let cancelled = false;
    setHistoryLoading(true);
    api.getConversationMessages(conversationId)
      .then(res => {
        if (cancelled) return;
        const msgs: Message[] = (res.data.data || []).map((m: ConversationMessage) => m as Message);
        setHistory(msgs);
        setStreamingText(''); // 历史已加载，清掉可能残留的流式框
        resetTypewriter();    // 会话切换：清理打字机 timer
      })
      .catch(e => console.error(e))
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [conversationId, resetTypewriter]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, streamingText, loading]);

  // 组件卸载时清理打字机 interval，防止泄漏
  useEffect(() => () => {
    if (typewriterTimerRef.current) {
      clearInterval(typewriterTimerRef.current);
      typewriterTimerRef.current = null;
    }
  }, []);

  // ---- actions ----

  /** Delete a single chat history entry. */
  const handleDelete = async (msgId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.deleteChatHistory(msgId);
      setHistory(prev => prev.filter(m => m.id !== msgId));
    } catch (err) {
      console.error('删除失败:', err);
    }
  };

  /** Cancel an in-flight streaming request. */
  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  /** Run the streaming SSE path. */
  const askStreaming = useCallback(async () => {
    const q = question.trim();
    setQuestion('');
    setError(null);
    setStreamingText('');
    setLoading(true);
    setToolStatus('');

    // Reset refs
    answerRef.current = '';
    sourcesRef.current = [];
    setStreamingSources([]);
    loadingRef.current = true;
    streamConvRef.current = conversationRef.current;
    streamCreatedConvRef.current = null;
    pendingIdRef.current = null;
    toolCallsRef.current = [];
    streamChatIdRef.current = null;
    tokenCountRef.current = 0;
    // 上一轮打字机若仍在播放（整段到达未落库），先落库再开新流，避免回答丢失
    if (typewriterActiveRef.current) {
      typewriterPendingRef.current = false;
      finalizeStream();
    }
    stopTypewriter();

    // 乐观 UI：发送瞬间立即显示提问气泡（answer 为空 = pending，done 后按 id 更新）
    const pendingId = Date.now();
    pendingIdRef.current = pendingId;
    setHistory(prev => [
      ...prev,
      {
        id: pendingId,
        question: q,
        answer: '',
        sources: [],
        created_at: new Date().toISOString(),
      } as Message,
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await api.chatStream(historyUpmasterId, q, (event: ChatStreamEvent) => {
        if (event.type === 'conversation') {
          // 仅在拿到合法数字 id 时动作，避免 Hub 会话帧把 conversation_id 置成 undefined
          const cid = event.conversation_id;
          if (typeof cid === 'number') {
            if (conversationRef.current !== cid) {
              conversationRef.current = cid;
              streamCreatedConvRef.current = cid;
              onConversationCreated(cid);
            }
          }
        } else if (event.type === 'sources') {
          sourcesRef.current = event.sources;
          setStreamingSources(event.sources);
        } else if (event.type === 'tool_calls') {
          for (const c of event.calls) {
            toolCallsRef.current.push({ name: c.name, args: c.args || {}, result: null });
          }
          const names = event.calls.map(c => {
            const nameMap: Record<string, string> = {
              record_training: '记录训练数据',
              query_training: '查询训练历史',
              get_calendar: '获取训练日历',
              generate_training_plan: '生成训练计划',
            };
            return nameMap[c.name] || c.name;
          });
          if (event.calls.some(c => c.name === 'record_training')) recordedTrainingRef.current = true;
          setToolStatus(`🔧 ${names.join('、')}...`);
        } else if (event.type === 'tool_results') {
          for (const r of event.results) {
            const tc = toolCallsRef.current.find(c => c.name === r.name && c.result == null);
            if (tc) {
              try {
                tc.result = JSON.stringify(r.result).slice(0, 500);
              } catch {
                tc.result = String(r.result).slice(0, 500);
              }
            }
          }
          setToolStatus('✅ 数据已处理');
          setTimeout(() => setToolStatus(''), 2000);
        } else if (event.type === 'status') {
          // 层3：上游网关异常切换本地引擎时短暂提示，不打断渲染
          setError(null);
          setToolStatus(`ℹ️ ${event.message}`);
          setTimeout(() => setToolStatus(''), 3000);
        } else if (event.type === 'error') {
          // 层3：SSE error 帧（此前被静默丢弃，界面卡死）——停止打字动画并提示
          stopTypewriter();
          setError('回答生成中断，请重试');
        } else if (event.type === 'token') {
          tokenCountRef.current += 1;
          answerRef.current += event.text;
          if (tokenCountRef.current === 1) {
            // 整段一次到达（deepseek-flash 常 1 个 token）：进入打字机 reveal，done 后继续播完
            startTypewriter(answerRef.current);
          } else {
            // 渐进式流：保持逐 token 渲染，不双重打字
            stopTypewriter();
            setStreamingText(answerRef.current);
          }
        } else if (event.type === 'done') {
          const final = event.full_answer || answerRef.current;
          answerRef.current = final;
          if (tokenCountRef.current === 0) {
            startTypewriter(final); // 无 token 事件、整段在 done 里：同样走打字机
          } else if (tokenCountRef.current === 1 && typewriterActiveRef.current) {
            typewriterFullRef.current = final; // 打字机进行中：同步完整文本，继续 reveal
          } else {
            setStreamingText(final);
          }
          if (event.chat_id != null) streamChatIdRef.current = event.chat_id;
        }
      }, 5, controller.signal, selectedIds.length ? selectedIds : undefined, conversationRef.current ?? undefined, coach?.id);

      // Done – 落库到 history（不重复 push；新对话时 conversation 事件已更新 conversationRef）。
      // 整段到达且打字机仍在播放时，等 reveal 完成再落库，避免动画被中断。
      if (typewriterActiveRef.current) {
        typewriterPendingRef.current = true;
      } else {
        finalizeStream();
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        stopTypewriter();
        setStreamingText('');
        setStreamingSources([]);
      } else {
        stopTypewriter();
        setError(err?.message || '请求失败，请重试');
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
      abortRef.current = null;
      // 打字机仍在播放时，落库所需 ref 由 finalizeStream 在 reveal 完成后读取，这里不能提前清空
      if (!typewriterActiveRef.current) {
        pendingIdRef.current = null;
        recordedTrainingRef.current = false;
      }
    }
  }, [historyUpmasterId, selectedIds, question, onConversationCreated, onConversationUpdated, coach, startTypewriter, stopTypewriter, finalizeStream]);

  /** Run the normal (non-streaming) axios path. */
  const askNormal = useCallback(async () => {
    const q = question.trim();
    setQuestion('');
    setError(null);
    setStreamingText('');
    setLoading(true);
    loadingRef.current = true;
    streamConvRef.current = conversationRef.current;

    // 乐观 UI：立即显示提问气泡；完成后 history 被服务器消息全量替换，pending 自然被覆盖
    const pendingId = Date.now();
    pendingIdRef.current = pendingId;
    setHistory(prev => [
      ...prev,
      {
        id: pendingId,
        question: q,
        answer: '',
        sources: [],
        created_at: new Date().toISOString(),
      } as Message,
    ]);

    try {
      const res = await api.chat(historyUpmasterId, q, 5, selectedIds.length ? selectedIds : undefined, conversationRef.current ?? undefined, coach?.id);
      const data = res.data;

      if (data.conversation_id && conversationRef.current !== data.conversation_id) {
        conversationRef.current = data.conversation_id;
        onConversationCreated(data.conversation_id);
      }

      if (data.success && streamConvRef.current === conversationRef.current && conversationRef.current) {
        const msgs = await api.getConversationMessages(conversationRef.current);
        setHistory((msgs.data.data || []).map((m: ConversationMessage) => m as Message));
      } else if (data.error) {
        setError(data.error);
      }
      onConversationUpdated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || '请求失败');
    } finally {
      setLoading(false);
      loadingRef.current = false;
      pendingIdRef.current = null;
    }
  }, [historyUpmasterId, selectedIds, question, onConversationCreated, onConversationUpdated, coach]);

  const handleAsk = () => {
    if (!question.trim() || loading) return;
    if (useStreaming) {
      askStreaming();
    } else {
      askNormal();
    }
  };

  const handleRetry = () => {
    setError(null);
  };

  /** 展开/收起某条消息的工具调用面板。 */
  const toggleToolCalls = (msgId: number) => {
    setOpenToolCalls(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  /** 展开/收起某条消息的参考资料折叠块。 */
  const toggleSources = (msgId: number) => {
    setOpenSources(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  /** 提交回答反馈：同按钮再点 → rating=0 删除；切换 👍↔👎 直接覆盖。 */
  const handleFeedback = async (chatId: number, rating: 1 | -1) => {
    if (!user) return;
    const current = feedbackSel[chatId];
    const next = current === rating ? 0 : rating;
    try {
      await api.submitFeedback(chatId, next);
      setFeedbackSel(prev => ({ ...prev, [chatId]: next }));
    } catch (err) {
      console.error('反馈失败:', err);
    }
  };



  const toggleSource = (id: number) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      return [...prev, id];
    });
  };

  const selectAll = () => setSelectedIds([]);

  // ---- JSX ----
  const singleId = selectedIds.length === 1 ? selectedIds[0] : 0;
  const selectedUpMaster = upmasters.find(u => u.id === singleId);
  const upmasterName = selectedIds.length === 0
    ? '全部知识库'
    : (selectedIds.length === 1 ? (selectedUpMaster?.name || '知识库') : `已选 ${selectedIds.length} 个数据源`);

  const isNewChat = conversationId == null;

  return (
    <div className="chat-container">
      {/* ── 左侧消息区（header + messages + input） ── */}
      <div className="chat-main">
        {/* ── Header ── */}
        <div className="chat-panel-header">
        <div>
          <h3>{isNewChat ? '新对话' : '💬 健身助手对话'}</h3>
          <p className="chat-panel-subtitle">{isCoach ? `与「${coach!.name}」的单窗口对话，仅基于其专属知识库回答` : '问我健身知识，或者直接告诉我训练内容，我帮你记录'}</p>
        </div>
        <div className="chat-header-actions">
          {isCoach || isFixed ? (
            <span
              className="upmaster-lock-badge"
              title={isCoach ? `锁定教练「${coach!.name}」，仅检索其绑定知识库` : '锁定当前 Up 主，仅基于该知识库问答'}
            >
              🔒 {isCoach ? coach!.name : upmasterName}
            </span>
          ) : (
            <div className="upmaster-multiselect">
              <button
                type="button"
                className="upmaster-select"
                onClick={() => setSelOpen(o => !o)}
              >
                {upmasterName} ▾
              </button>
              {selOpen && (
                <div className="upmaster-multiselect-panel">
                  <label className="ms-option">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === 0}
                      onChange={selectAll}
                    />
                    <span>🌐 全部知识库</span>
                  </label>
                  {upmasters.map(um => (
                    <label key={um.id} className="ms-option">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(um.id)}
                        onChange={() => toggleSource(um.id)}
                      />
                      <span>{um.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <label className="stream-toggle">
            <input
              type="checkbox"
              checked={useStreaming}
              onChange={(e) => setUseStreaming(e.target.checked)}
            />
            <span>流式输出</span>
          </label>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="chat-messages">
        {isNewChat && history.length === 0 && !loading && !streamingText && (
          <div className="empty-state m-auto">
            <div className="empty-state-icon">💪</div>
            <h3 className="empty-state-title">开始你的健身问答</h3>
            <p className="empty-state-desc">
              基于健身博主视频字幕回答，也可以直接记录训练、查日历、生成计划
            </p>
            <div className="suggestion-grid">
              {SUGGESTIONS.map(s => (
                <button
                  key={s.text}
                  className="suggestion-card"
                  onClick={() => { setQuestion(s.text); }}
                >
                  <span className="suggestion-icon text-lg shrink-0">{s.icon}</span>
                  <span>{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {!isNewChat && history.length === 0 && !loading && !streamingText && !historyLoading && (
          <div className="empty-state m-auto">
            <div className="empty-state-icon">💬</div>
            <h3 className="empty-state-title">这个对话还是空的</h3>
            <p className="empty-state-desc">输入问题开始聊天吧</p>
          </div>
        )}

        {historyLoading && (
          <div className="chat-message fade-in">
            <div className="chat-bubble chat-bubble-assistant">
              <div className="loading-skeleton">
                <span className="skel-bar" />
                <span className="skel-bar short" />
              </div>
            </div>
          </div>
        )}

        {/* --- History --- */}
        {history.map((msg) => (
          <div key={msg.id} className="chat-message fade-in">
            {/* Question bubble */}
            <div className="chat-bubble chat-bubble-user">
              <div className="chat-bubble-text">{msg.question}</div>
              <button
                className="chat-delete-btn"
                onClick={(e) => handleDelete(msg.id, e)}
                title="删除此对话"
              >
                ✕
              </button>
            </div>

            {/* Answer bubble：pending（answer 为空）时显示 loading skeleton，token 开始后由 streamingText 气泡接管 */}
            {msg.answer ? (
              <div className="chat-bubble chat-bubble-assistant">
                <div className="chat-bubble-text md-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.answer}</ReactMarkdown>
                </div>
              </div>
            ) : (
              loading && !streamingText && (
                <div className="chat-bubble chat-bubble-assistant">
                  <div className="loading-skeleton">
                    <span className="skel-bar" />
                    <span className="skel-bar short" />
                    <span className="skel-bar medium" />
                  </div>
                </div>
              )
            )}

            {/* 工具调用折叠条（默认收起，展开看 name/args/result）+ 回答反馈 👍/👎 */}
            {msg.answer && (
              <>
                {msg.sources && msg.sources.length > 0 && (
                  <div className="chat-tools-block">
                    <button
                      type="button"
                      className="chat-tools-toggle"
                      onClick={() => toggleSources(msg.id)}
                    >
                      📚 {dedupeSources(msg.sources).length} 条参考视频
                      <span className="chat-tools-caret">{openSources.has(msg.id) ? '▾' : '▸'}</span>
                    </button>
                    {openSources.has(msg.id) && (
                      <div className="chat-tools-panel">
                        {dedupeSources(msg.sources).map((s, i) => (
                          <SourceItem key={s.url || s.bvid || i} source={s} index={i} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {user && msg.tool_calls && msg.tool_calls.length > 0 && (
                  <div className="chat-tools-block">
                    <button
                      type="button"
                      className="chat-tools-toggle"
                      onClick={() => toggleToolCalls(msg.id)}
                    >
                      🛠️ 调用了 {msg.tool_calls.length} 个工具
                      <span className="chat-tools-caret">{openToolCalls.has(msg.id) ? '▾' : '▸'}</span>
                    </button>
                    {openToolCalls.has(msg.id) && (
                      <div className="chat-tools-panel">
                        {msg.tool_calls.map((tc, i) => (
                          <div key={i} className="chat-tool-item">
                            <code className="chat-tool-signature" title={JSON.stringify(tc.args, null, 2)}>
                              {formatToolInvocation(tc.name, tc.args)}
                            </code>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {user && msg.id > 0 && msg.id < 1000000000 && (
                  <div className="chat-feedback-bar">
                    <button
                      type="button"
                      className={`chat-feedback-btn${feedbackSel[msg.id] === 1 ? ' active' : ''}`}
                      onClick={() => handleFeedback(msg.id, 1)}
                      title="回答有帮助"
                    >
                      👍
                    </button>
                    <button
                      type="button"
                      className={`chat-feedback-btn${feedbackSel[msg.id] === -1 ? ' active' : ''}`}
                      onClick={() => handleFeedback(msg.id, -1)}
                      title="回答不准确"
                    >
                      👎
                    </button>
                  </div>
                )}
              </>
            )}

          </div>
        ))}

        {/* --- Streaming answer (live) --- */}
        {toolStatus && (
          <div className="chat-message fade-in">
            <div className="tool-status-banner">{toolStatus}</div>
          </div>
        )}
        {streamingText && (
          <div className="chat-message fade-in">
            <div
              className={`chat-bubble chat-bubble-assistant${typewriterActive ? ' chat-bubble-typewriter' : ''}`}
              onClick={typewriterActive ? revealAllTypewriter : undefined}
              title={typewriterActive ? '点击显示全部' : undefined}
            >
              <div className="chat-bubble-text md-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
              </div>
              {(loading || typewriterActive) && <span className="typing-cursor" />}
            </div>
            {streamingSources.length > 0 && (
              <div className="chat-tools-block">
                <button
                  type="button"
                  className="chat-tools-toggle"
                  onClick={() => toggleSources(-1)}
                >
                  📚 {dedupeSources(streamingSources).length} 条参考视频
                  <span className="chat-tools-caret">{openSources.has(-1) ? '▾' : '▸'}</span>
                </button>
                {openSources.has(-1) && (
                  <div className="chat-tools-panel">
                    {dedupeSources(streamingSources).map((s, i) => (
                      <SourceItem key={s.url || s.bvid || i} source={s} index={i} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* --- Error + Retry --- */}
        {error && (
          <div className="error-banner">
            <span>⚠ {error}</span>
            <button className="btn-retry" onClick={handleRetry}>
              重试
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input ── */}
      <div className="chat-input-container">
        {tools.length > 0 && (
          <div className="chat-tools-bar">
            <span className="chat-tools-label">🛠️ 我支持：</span>
            {tools.map(t => (
              <span key={t.name} className="chat-tool-chip" title={t.description}>
                {TOOL_LABELS[t.name] || t.name}
              </span>
            ))}
          </div>
        )}
        <div className="chat-input-wrapper">
          <textarea
            className="chat-input"
            placeholder="输入健身相关问题...（Enter 发送，Shift+Enter 换行）"
            value={question}
            rows={1}
            onChange={(e) => {
              setQuestion(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
            disabled={loading}
          />
          {(loading || typewriterActive) && useStreaming ? (
            <button
              className="btn btn-cancel shrink-0 px-4 py-2.5"
              onClick={typewriterActive ? revealAllTypewriter : cancelStream}
              style={{ borderRadius: '9999px' }}
            >
              {typewriterActive ? '⏩ 显示全部' : '⏹ 停止'}
            </button>
          ) : (
            <button
              className="btn btn-brand shrink-0 px-5 py-2.5"
              onClick={handleAsk}
              disabled={loading || !question.trim()}
              style={{ borderRadius: '9999px' }}
            >
              {loading ? '⏳' : '➤'}
            </button>
          )}
        </div>
          <p className="chat-input-hint">
            基于 {upmasterName} 的视频字幕回答 · 也可以直接说训练内容
          </p>
        </div>
      </div>

    </div>
  );
};

export default ChatPanel;
